import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

const WizardStepAmount = ({ onNext, onBack, setWizardData, wizardData }) => {
  const [selectedAmount, setSelectedAmount] = useState(wizardData.amount_per_period || null);
  const [totalCost, setTotalCost] = useState(0);
  const [paymentsNeeded, setPaymentsNeeded] = useState(0);
  const metalPrices = useSelector(state => state.metalPrices.prices);

  // Payment amount options
  const paymentOptions = [
    { amount: 10, display: '$10', icon: '💵', description: 'Small starter amount' },
    { amount: 25, display: '$25', icon: '💴', description: 'Light investment' },
    { amount: 50, display: '$50', icon: '💶', description: 'Popular choice' },
    { amount: 100, display: '$100', icon: '💷', description: 'Solid investment' },
    { amount: 200, display: '$200', icon: '💸', description: 'Serious commitment' },
    { amount: 250, display: '$250', icon: '💰', description: 'Premium investment' }
  ];

  useEffect(() => {
    // Calculate total cost needed to reach target weight
    if (wizardData.target_weight && wizardData.weight_type && wizardData.metal_type && metalPrices.length > 0) {
      const selectedMetal = metalPrices.find(p => p.metal_type === wizardData.metal_type);
      if (selectedMetal) {
        let pricePerUnit;

        // Get the appropriate price based on weight unit
        if (wizardData.weight_type === 'oz') {
          pricePerUnit = selectedMetal.price_per_ounce;
        } else if (wizardData.weight_type === 'g') {
          pricePerUnit = selectedMetal.price_per_gram;
        } else if (wizardData.weight_type === 'kg') {
          pricePerUnit = selectedMetal.price_per_gram * 1000; // Convert grams to kg
        }

        if (pricePerUnit > 0) {
          const cost = wizardData.target_weight * pricePerUnit;
          setTotalCost(cost);

          // Calculate payments needed if amount is selected
          if (selectedAmount > 0) {
            const payments = Math.ceil(cost / selectedAmount);
            setPaymentsNeeded(payments);
          }
        }
      }
    }
  }, [wizardData.target_weight, wizardData.weight_type, wizardData.metal_type, metalPrices, selectedAmount]);

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setWizardData({
      ...wizardData,
      amount_per_period: amount
    });

    // Recalculate payments needed
    if (totalCost > 0) {
      const payments = Math.ceil(totalCost / amount);
      setPaymentsNeeded(payments);
    }
  };

  const handleNext = () => {
    if (selectedAmount && selectedAmount > 0) {
      onNext();
    }
  };

  const getWeightUnitDisplay = () => {
    switch(wizardData.weight_type) {
      case 'g': return 'grams';
      case 'oz': return 'troy ounces';
      case 'kg': return 'kilograms';
      default: return wizardData.weight_type;
    }
  };

  const getTimeToTarget = () => {
    if (!paymentsNeeded || !wizardData.period) return 'N/A';

    let periodInDays;
    switch(wizardData.period) {
      case 'weekly':
        periodInDays = 7;
        break;
      case 'bi-weekly':
        periodInDays = 14;
        break;
      case 'monthly':
        periodInDays = 30;
        break;
      case 'quarterly':
        periodInDays = 90;
        break;
      default:
        periodInDays = 30;
    }

    const totalDays = paymentsNeeded * periodInDays;

    if (totalDays < 30) {
      return `${totalDays} days`;
    } else if (totalDays < 365) {
      return `${Math.ceil(totalDays / 30)} months`;
    } else {
      return `${(totalDays / 365).toFixed(1)} years`;
    }
  };

  return (
    <div className="min-h-[450px] bg-gradient-to-br from-gray-50 to-gray-200 py-3 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Step Description */}
        <h2 className="text-center text-2xl font-bold text-gray-800 mb-2">Select your deposit amount</h2>
        <p className="text-center text-gray-600 mb-4">Set your {wizardData.period?.replace('-', ' ')} contribution towards your goal.</p>

        {/* Content Section */}
        <div className="flex justify-center">
          <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paymentOptions.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleAmountSelect(option.amount)}
                  className={`
                    relative bg-white rounded-2xl p-5 cursor-pointer
                    transition-all duration-300 ease-in-out
                    border-2 shadow-md min-h-[240px]
                    hover:transform hover:-translate-y-1 hover:shadow-xl
                    ${selectedAmount === option.amount
                      ? 'border-blue-500 shadow-blue-200 transform -translate-y-1'
                      : 'border-gray-200 hover:border-blue-500'
                    }
                  `}
                >
                  {selectedAmount === option.amount && (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-2xl"></div>
                  )}

                  <div className="relative z-10 flex flex-col items-center text-center h-full">
                    <div className="text-4xl mb-2 opacity-80 transition-opacity duration-300 hover:opacity-100">
                      {option.icon}
                    </div>

                    <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                      {option.display}
                    </h3>

                    <p className="text-sm text-gray-600 leading-snug flex-grow">
                      {option.description}
                    </p>

                    {selectedAmount === option.amount && paymentsNeeded > 0 && (
                      <div className="mt-3">
                        <span className="inline-block px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full">
                          {paymentsNeeded} payments needed
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {selectedAmount && totalCost > 0 && (
              <div className="mt-4 bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 border-2 border-blue-500 shadow-lg">
                <h5 className="text-center mb-4 text-gray-800 font-semibold">
                  <i className="bi bi-calculator mr-2"></i>
                  Payment Plan Summary
                </h5>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-4 rounded-lg bg-white/70">
                    <div className="text-sm text-gray-600 font-medium mb-1">Target</div>
                    <div className="text-base font-semibold text-gray-800">
                      {wizardData.target_weight} {getWeightUnitDisplay()} of {wizardData.metal_type}
                    </div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-white/70">
                    <div className="text-sm text-gray-600 font-medium mb-1">Total Cost</div>
                    <div className="text-base font-semibold text-gray-800">
                      ${totalCost.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-white/70">
                    <div className="text-sm text-gray-600 font-medium mb-1">Payment Amount</div>
                    <div className="text-base font-semibold text-gray-800">
                      ${selectedAmount} per {wizardData.period}
                    </div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-white/70">
                    <div className="text-sm text-gray-600 font-medium mb-1">Time to Target</div>
                    <div className="text-base font-semibold text-gray-800">
                      {getTimeToTarget()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Section */}
        <div className="flex justify-between mt-4">
          <button
            onClick={onBack}
            className="bg-white text-black px-6 py-2 rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors"
          >
            <i className="bi bi-arrow-left mr-2"></i>
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={!selectedAmount || selectedAmount <= 0}
            className={`px-6 py-2 rounded-full font-bold transition-colors ${
              selectedAmount && selectedAmount > 0
                ? 'bg-black text-white hover:bg-gray-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Review Subscription
            <i className="bi bi-arrow-right ml-2"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardStepAmount;
